<!-- _Generated with AI_ -->

<script setup lang="ts">
import { reactive, ref } from 'vue';
import axios from 'axios';
import { usStates } from '@/shared/usStates';
import { referralTypes } from '@/shared/referralTypes';

const props = defineProps<{
  product: 'school' | 'homeschool' | 'district';
}>();

const emit = defineEmits(['submitted']);

const form = reactive({
  firstname: '',
  lastname: '',
  email: '',
  phone: '',
  school: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  title: '',
  subscription_count: 1,
  referral: '',
  referralCode: '',
  district: '',
  students: [] as { firstname: string; lastname?: string }[],
  marketingConsent: false,
});

const submitting = ref(false);
const successMessage = ref('');
const errorMessage = ref('');

const MAX_STUDENTS = 5;

function addStudent() {
  if (form.students.length >= MAX_STUDENTS) return;
  form.students.push({ firstname: '', lastname: '' });
}

function removeStudent(index: number) {
  form.students.splice(index, 1);
}

async function onSubmit() {
  submitting.value = true;
  successMessage.value = '';
  errorMessage.value = '';

  try {
    const payload: Record<string, unknown> = {
      firstname: form.firstname,
      lastname: form.lastname,
      email: form.email,
      phone: form.phone,
      product: props.product,
    };

    if (props.product === 'school' || props.product === 'homeschool') {
      payload.school = form.school;
      payload.address = form.address;
      payload.city = form.city;
      payload.state = form.state;
      payload.zip = form.zip;
      payload.title = form.title;
      payload.referral = form.referral || undefined;
      payload.referralCode = form.referralCode || undefined;
    }

    if (props.product === 'school') {
      payload.subscription_count = Number(form.subscription_count);
    }

    if (props.product === 'homeschool') {
      payload.subscription_count = form.students.length;
      payload.students = form.students.map(s => ({
        firstname: s.firstname,
        lastname: s.lastname || undefined,
      }));
    }

    if (props.product === 'district') {
      payload.district = form.district;
      payload.title = form.title;
      payload.marketing_consent = form.marketingConsent;
    }

    const response = await axios.post('/api/v2/quote', payload);

    if (response.data.success) {
      successMessage.value = 'Thank you for your interest! We will contact you soon.';
      emit('submitted');
    } else {
      errorMessage.value = response.data.message ?? 'An error occurred.';
    }
  } catch (error: any) {
    const errors = error.response?.data?.errors ?? {};
    const messages = Object.values(errors).flat().filter(Boolean) as string[];
    errorMessage.value = messages.join('\n') || 'An error occurred. Please try again.';
  } finally {
    submitting.value = false;
  }
}
</script>

<template>
  <div>
    <div v-if="successMessage" class="alert alert-success">
      {{ successMessage }}
    </div>

    <form v-if="!successMessage" @submit.prevent="onSubmit" novalidate>
      <div v-if="errorMessage" class="alert alert-danger">
        <b>Error:</b> {{ errorMessage }}
      </div>

      <!-- Common fields -->
      <div class="row gx-3">
        <div :class="product === 'district' ? 'col-12' : 'col-sm-6'">
          <div class="mb-2 form-group">
            <label class="form-label">First Name *</label>
            <input
              type="text" class="form-control"
              v-model.trim="form.firstname" required
              maxlength="50" name="firstname"
            >
          </div>
        </div>
        <div :class="product === 'district' ? 'col-12' : 'col-sm-6'">
          <div class="mb-2 form-group">
            <label class="form-label">Last Name *</label>
            <input
              type="text" class="form-control"
              v-model.trim="form.lastname" required
              maxlength="50" name="lastname"
            >
          </div>
        </div>
      </div>

      <div class="mb-2 form-group">
        <label class="form-label">Email Address *</label>
        <input
          type="email" class="form-control"
          v-model="form.email" required
          maxlength="100" name="email"
        >
      </div>

      <div class="mb-2 form-group">
        <label class="form-label">Phone *</label>
        <input
          type="tel" class="form-control"
          v-model="form.phone" required
          maxlength="20" name="phone"
        >
      </div>

      <!-- School / Homeschool fields -->
      <template v-if="product === 'school' || product === 'homeschool'">
        <div class="mb-2 form-group">
          <label class="form-label">Name of School *</label>
          <input
            type="text" class="form-control" name="school_name"
            v-model.trim="form.school" required
            maxlength="500"
          >
        </div>

        <div class="mb-2 form-group">
          <label class="form-label">Address</label>
          <input
            type="text" class="form-control" name="school_address"
            v-model.trim="form.address"
            maxlength="800"
          >
        </div>

        <div class="row gx-3">
          <div class="col-sm-6">
            <div class="mb-2 form-group">
              <label class="form-label">City</label>
              <input
                type="text" class="form-control" name="school_city"
                v-model.trim="form.city"
                maxlength="100"
              >
            </div>
          </div>
          <div class="col-sm-6">
            <div class="mb-2 form-group">
              <label class="form-label">State</label>
              <select class="form-control" v-model="form.state">
                <option value="">Please select one</option>
                <option v-for="s in usStates" :key="s.code" :value="s.code">
                  {{ s.name }}
                </option>
              </select>
            </div>
          </div>
        </div>

        <div class="row gx-3">
          <div class="offset-sm-6 col-sm-6">
            <div class="mb-2 form-group">
              <label class="form-label">Zip Code</label>
              <input
                type="text" class="form-control" name="school_zip"
                v-model.trim="form.zip"
                maxlength="10"
              >
            </div>
          </div>
        </div>
      </template>

      <!-- District field -->
      <template v-if="product === 'district'">
        <div class="mb-2 form-group">
          <label class="form-label">District *</label>
          <input
            type="text" class="form-control" name="district_name"
            v-model.trim="form.district" required
            maxlength="500"
          >
        </div>
      </template>

      <!-- Job Title (all variants) -->
      <div class="mb-2 form-group">
        <label class="form-label">Job Title</label>
        <input
          type="text" class="form-control" name="job_title"
          v-model="form.title"
          maxlength="200"
        >
      </div>

      <!-- Subscription count (school only) -->
      <div v-if="product === 'school'" class="mb-2 form-group">
        <label class="form-label">Number of Teacher Subscriptions Requested *</label>
        <input
          type="number" class="form-control"
          v-model.number="form.subscription_count" min="1" required
        >
      </div>

      <!-- Student names (homeschool only) -->
      <template v-if="product === 'homeschool'">
        <div v-for="(student, index) in form.students" :key="index" class="row gx-3 align-items-end mb-2 student-fields">
          <div class="col-sm-5">
            <div class="form-group">
              <input
                v-model.trim="form.students[index].firstname"
                type="text" required class="form-control"
                :placeholder="`Student #${index + 1} First Name`"
                maxlength="50"
              >
            </div>
          </div>
          <div class="col-sm-5">
            <div class="form-group">
              <input
                v-model.trim="form.students[index].lastname"
                type="text" class="form-control"
                :placeholder="`Student #${index + 1} Last Name`"
                maxlength="50"
              >
            </div>
          </div>
          <div class="col-sm-2">
            <button
              type="button" class="btn btn-outline-danger btn-sm"
              @click="removeStudent(index)"
              title="Remove student"
            >❌</button>
          </div>
        </div>

        <div class="mb-2">
          <button
            type="button"
            :class="{ disabled: form.students.length >= MAX_STUDENTS }"
            class="btn btn-success btn-sm"
            :disabled="form.students.length >= MAX_STUDENTS"
            @click="addStudent"
          >Add Student</button>
          <small v-if="form.students.length > 0" class="text-muted ms-2">
            Students: {{ form.students.length }} of {{ MAX_STUDENTS }}
          </small>
        </div>
      </template>

      <!-- Referral (school + homeschool) -->
      <template v-if="product === 'school' || product === 'homeschool'">
        <div class="mb-2 form-group">
          <label class="form-label">How Did You Find Out About Us?</label>
          <select class="form-select" v-model="form.referral">
            <option value="">--Select a choice--</option>
            <option v-for="r in referralTypes" :key="r" :value="r">{{ r }}</option>
          </select>
        </div>

        <div class="mb-2 form-group">
          <label class="form-label">Referral Code (optional)</label>
          <input
            type="text" class="form-control"
            v-model="form.referralCode"
            maxlength="30"
          >
        </div>
      </template>

      <!-- District marketing consent -->
      <template v-if="product === 'district'">
        <div class="mb-3">
          <b-form-checkbox v-model="form.marketingConsent">
            Yes, I'd like to receive occasional marketing emails from Lyrics2Learn.
            I have the right to opt out at any time.
            <a href="/privacy?embed=1" target="_blank">View privacy policy.</a>
          </b-form-checkbox>
        </div>
      </template>

      <!-- Submit -->
      <div class="d-grid gap-2 mt-3">
        <button
          type="submit" class="btn btn-primary btn-lg"
          :disabled="submitting"
        >
          <span v-if="submitting">Sending...</span>
          <span v-else>Submit</span>
        </button>
      </div>
    </form>
  </div>
</template>

<style lang="scss" scoped>
label.form-label {
  font-size: 14px;
}
</style>
